/**
 * A instrução da atividade tem conteúdo para mostrar?
 *
 * Fonte única para jornada e quiz. Antes eram duas cópias — uma em
 * `features/journeys/Levels/utils`, outra em `features/quizzes/Show/utils` —
 * que já tinham divergido entre si: a do quiz esquecia o `.trim()` no ramo de
 * fallback, então `<p> </p>` passava lá e não passava aqui.
 *
 * **A pergunta é "renderiza alguma coisa?", não "sobra texto?".** Medir só
 * texto foi o que descartou em silêncio a instrução composta apenas de imagem:
 * `LevelPlayIntro` renderiza o HTML e estiliza imagem de propósito
 * (`[&_img]:w-full [&_img]:block`), então uma instrução que é só uma imagem
 * satisfaz o renderizador e era rejeitada pelo detector. Dezessete desafios
 * estavam nesse estado, em cinco eventos.
 *
 * **`iframe` não conta**, e a exceção tem dono: `LevelPlayIntro` passa pelo
 * `sanitizeHtml`, que remove iframe (só `sanitizeEmbedHtml` o mantém). Contar
 * iframe aqui criaria um step de introdução em branco — trocar um sintoma por
 * outro.
 *
 * **Os dois ramos aplicam a mesma regra, de propósito.** O ramo sem DOM não é
 * um segundo critério: é o mesmo critério com outro instrumento. A versão
 * anterior divergia — o fallback aceitava a instrução só com imagem — e o
 * efeito era pior que o bug: um teste unitário do caso quebrado **passava** no
 * projeto vitest `node`, onde `DOMParser` não existe, enquanto o navegador
 * seguia descartando. Por isso o teste desta função mora no projeto
 * `components`.
 */

/** Tags que o `sanitizeHtml` preserva e o navegador desenha sozinho. */
const RENDERABLE_MEDIA = 'img, picture, svg, video, audio'
const RENDERABLE_MEDIA_PATTERN = /<\s*(img|picture|svg|video|audio)\b/i

type WithIntro = { intro?: string | null } | null | undefined

const hasRenderableText = (html: string): boolean => {
  const text =
    typeof DOMParser === 'undefined'
      ? html.replace(/<[^>]*>/g, ' ')
      : new DOMParser().parseFromString(html, 'text/html').body.textContent || ''

  // `&nbsp;` sai como ` ` do parser e literal do replace — `\s` cobre o
  // primeiro, a alternativa cobre o segundo.
  return text.replace(/&nbsp;|\s+/g, '').length > 0
}

const hasRenderableMedia = (html: string): boolean => {
  if (typeof DOMParser === 'undefined') return RENDERABLE_MEDIA_PATTERN.test(html)

  const doc = new DOMParser().parseFromString(html, 'text/html')

  return doc.body.querySelector(RENDERABLE_MEDIA) !== null
}

export const hasIntro = (activity: WithIntro): boolean => {
  const intro = activity?.intro

  if (typeof intro !== 'string' || !intro.trim()) return false

  return hasRenderableMedia(intro) || hasRenderableText(intro)
}
